import React, { useState } from "react";
import { StyleSheet, View, ActivityIndicator } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedInput } from "@/components/ThemedInput";
import { CustomAlert } from "@/components/ThemedAlert";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@/components/firebase/useFirebase";
import { terminateAccount } from "@/services/firebase/user-functions";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

/**
 * Screen providing users the Right of Deletion through irreversible account termination.
 * Informs the user of the consequences, preserves memories with active co-owners,
 * purges all solely owned resources, and disables the user's account.
 *
 * @returns {React.ReactElement} The rendered TerminateAccountScreen.
 */
export default function TerminateAccountScreen(): React.ReactElement {
  const { signOut } = useAuth();
  const services = useFirebase();

  const [confirmationInput, setConfirmationInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isConfirmed =
    confirmationInput.trim().toUpperCase() === "TERMINATE MY ACCOUNT. ALL IS LOST.";

  /**
   * Handles the execution of account termination upon user confirmation.
   */
  const handleConfirmTermination = async (): Promise<void> => {
    if (!isConfirmed) {
      setErrorMessage("Please type 'TERMINATE MY ACCOUNT. ALL IS LOST.' exactly to confirm.");
      return;
    }

    if (!services) {
      CustomAlert.alert("Service Error", "Firebase services are currently unavailable.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const response = await terminateAccount(services, {
        confirmationText: confirmationInput.trim().toUpperCase(),
      });

      if (response && response.status) {
        CustomAlert.alert(
          "Account Terminated",
          "Your account and all associated personal data have been permanently deleted.",
          [
            {
              text: "OK",
              onPress: async () => {
                try {
                  await signOut();
                } catch (e) {
                  // Ignore sign out errors during post-termination cleanup
                }
                router.replace("/");
              },
            },
          ]
        );
      } else {
        const err = response?.error || "Failed to terminate account. Please try again or contact support.";
        setErrorMessage(err);
        CustomAlert.alert("Termination Failed", err);
      }
    } catch (err: any) {
      const msg = err?.message || "An unexpected error occurred while terminating your account.";
      setErrorMessage(msg);
      CustomAlert.alert("Error", msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemedScreen
      title="Terminate Account"
      leftIcon={{
        name: "arrow-back",
        onPress: () => {
          if (router.canGoBack()) {
            router.back();
          } else {
            router.replace("/(tabs)/profile" as any);
          }
        },
      }}
    >
      <ThemedView style={styles.container}>
        {/* Warning Banner */}
        <View style={styles.warningCard}>
          <View style={styles.warningHeaderRow}>
            <Ionicons name="warning" size={28} color="#DC2626" />
            <ThemedText style={styles.warningTitle}>Permanent & Irreversible Action</ThemedText>
          </View>
          <ThemedText style={styles.warningText}>
            Terminating your account under the Right of Deletion permanently deletes your personal identity,
            profile, and owned data. This action cannot be undone or restored by anyone.
          </ThemedText>
        </View>

        {/* Detailed Explanation */}
        <ThemedView style={styles.detailsCard}>
          <ThemedText type="defaultSemiBold" style={styles.sectionHeader}>
            What happens when you terminate:
          </ThemedText>

          <View style={styles.bulletRow}>
            <Ionicons name="close-circle-outline" size={20} color="#DC2626" style={styles.bulletIcon} />
            <ThemedText style={styles.bulletText}>
              <ThemedText style={styles.bulletBold}>Account Deactivation:</ThemedText> Your account is set to inactive and terminated. You will be signed out immediately and blocked from signing in again.
            </ThemedText>
          </View>

          <View style={styles.bulletRow}>
            <Ionicons name="trash-outline" size={20} color="#DC2626" style={styles.bulletIcon} />
            <ThemedText style={styles.bulletText}>
              <ThemedText style={styles.bulletBold}>Solely Owned Memories:</ThemedText> Any memory where you are the sole owner will be permanently and irreversibly deleted along with all its shares.
            </ThemedText>
          </View>

          <View style={styles.bulletRow}>
            <Ionicons name="people-outline" size={20} color="#16A34A" style={styles.bulletIcon} />
            <ThemedText style={styles.bulletText}>
              <ThemedText style={styles.bulletBold}>Co-Owned Memories Preserved:</ThemedText> If a memory is co-owned with other active members, it will NOT be deleted. Ownership will be reassigned to the remaining co-owners so shared history is preserved.
            </ThemedText>
          </View>

          <View style={styles.bulletRow}>
            <Ionicons name="chatbubbles-outline" size={20} color="#DC2626" style={styles.bulletIcon} />
            <ThemedText style={styles.bulletText}>
              <ThemedText style={styles.bulletBold}>Groups, Contacts & Messages:</ThemedText> All your created groups, address book contacts, messages, and shares will be purged.
            </ThemedText>
          </View>
        </ThemedView>

        {/* Confirmation Input Section */}
        <ThemedView style={styles.confirmationSection}>
          <ThemedText style={styles.confirmationPrompt}>
            To confirm termination, type <ThemedText style={styles.terminateKeyword}>TERMINATE MY ACCOUNT. ALL IS LOST.</ThemedText> below:
          </ThemedText>

          <ThemedInput
            value={confirmationInput}
            onChangeText={(val) => {
              setConfirmationInput(val);
              if (errorMessage) setErrorMessage("");
            }}
            placeholder="Type 'TERMINATE MY ACCOUNT. ALL IS LOST.' to confirm"
            autoCapitalize="characters"
            autoCorrect={false}
            error={errorMessage}
            editable={!submitting}
          />
        </ThemedView>

        {/* Action Buttons */}
        <ThemedView style={styles.actionsContainer}>
          {submitting ? (
            <View style={styles.loaderContainer}>
              <ActivityIndicator size="large" color="#DC2626" />
              <ThemedText style={styles.loadingText}>Terminating account and purging data...</ThemedText>
            </View>
          ) : (
            <>
              <ThemedButton
                title="Permanently Terminate Account"
                variant="danger"
                onPress={handleConfirmTermination}
                disabled={!isConfirmed || submitting}
                style={[styles.terminateBtn, !isConfirmed && styles.disabledBtn]}
              />

              <ThemedButton
                title="Cancel and Keep Account"
                variant="minimal"
                onPress={() => {
                  if (router.canGoBack()) {
                    router.back();
                  } else {
                    router.replace("/(tabs)/profile" as any);
                  }
                }}
                style={styles.cancelBtn}
              />
            </>
          )}
        </ThemedView>
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 20,
    maxWidth: 680,
    alignSelf: "center",
    width: "100%",
  },
  warningCard: {
    backgroundColor: "rgba(220, 38, 38, 0.08)",
    borderWidth: 1.5,
    borderColor: "#DC2626",
    borderRadius: 12,
    padding: 16,
    gap: 10,
  },
  warningHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  warningTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#DC2626",
  },
  warningText: {
    fontSize: 14,
    lineHeight: 20,
    color: "#B91C1C",
  },
  detailsCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(161, 174, 209, 0.3)",
    gap: 14,
  },
  sectionHeader: {
    fontSize: 16,
    marginBottom: 4,
  },
  bulletRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  bulletIcon: {
    marginTop: 2,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  bulletBold: {
    fontWeight: "700",
  },
  confirmationSection: {
    gap: 10,
  },
  confirmationPrompt: {
    fontSize: 14,
    lineHeight: 20,
  },
  terminateKeyword: {
    fontWeight: "700",
    color: "#DC2626",
  },
  actionsContainer: {
    gap: 12,
    marginTop: 10,
  },
  terminateBtn: {
    backgroundColor: "#DC2626",
  },
  disabledBtn: {
    opacity: 0.4,
  },
  cancelBtn: {
    marginTop: 4,
  },
  loaderContainer: {
    alignItems: "center",
    paddingVertical: 16,
    gap: 10,
  },
  loadingText: {
    fontSize: 14,
    color: "#DC2626",
    fontWeight: "600",
  },
});
